import { useEffect, useState } from 'react';
import customerAPI from '../api/customerAPI';

const emptyCustomer = {
  name: '',
  customerType: 'trucking',
  email: '',
  phone: '',
  mobile: '',
  address: '',
  city: '',
  province: '',
  zipCode: ''
};

export default function CustomerForm({ customerId, onSaved, onCancel }) {
  const [form, setForm] = useState(emptyCustomer);
  const [loading, setLoading] = useState(false);
  const isEdit = !!customerId;

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      customerAPI
        .getById(customerId)
        .then(res => setForm({ ...emptyCustomer, ...res.data }))
        .finally(() => setLoading(false));
    }
  }, [customerId, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await customerAPI.update(customerId, form);
      } else {
        await customerAPI.create(form);
      }
      onSaved?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      
      {/* BASIC INFO */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Customer Name *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            className="input w-full"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Customer Type</label>
          <select
            name="customerType"
            value={form.customerType}
            onChange={handleChange}
            className="input w-full"
            disabled={loading}
          >
            <option value="pos">POS Customer</option>
            <option value="trucking">Trucking</option>
            <option value="freight_forwarding">Freight Forwarding</option>
          </select>
        </div>
      </div>

      {/* CONTACT INFO */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input w-full"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Phone (Landline)</label>
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="input w-full"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Mobile Number</label>
          <input
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            className="input w-full"
            disabled={loading}
          />
        </div>
      </div>

      {/* ADDRESS */}
      <div>
        <label className="block text-sm font-medium mb-1">Address (optional)</label>
        <textarea
          name="address"
          value={form.address}
          onChange={handleChange}
          rows={2}
          placeholder="e.g. 123 Main St, Brgy Centro"
          className="input w-full"
          disabled={loading}
        />
        <p className="text-xs text-gray-400 mt-1">Used for BIR RELIEF DAT export (Substreet, Street, Barangay field)</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">City</label>
          <input
            name="city"
            value={form.city}
            onChange={handleChange}
            className="input w-full"
            placeholder="e.g. ILOILO CITY"
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Province</label>
          <input
            name="province"
            value={form.province}
            onChange={handleChange}
            className="input w-full"
            placeholder="e.g. ILOILO"
            disabled={loading}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Zip Code</label>
          <input
            name="zipCode"
            value={form.zipCode}
            onChange={handleChange}
            className="input w-full"
            placeholder="e.g. 5000"
            disabled={loading}
          />
        </div>
      </div>

      {/* ACTIONS */}
      <div className="flex justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          Cancel
        </button>

        <button
          type="submit"
          className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEdit ? 'Update Customer' : 'Create Customer'}
        </button>
      </div>
    </form>
  );
}
